<template>
  <div class="content-section bill-tariff">
    <p class="tariff-amount" :class="{ 'editable': isEditableAmount }" @click="onEditAmount(tariff.id)">
      <template v-if="isPurchaseBillTicket">
        <span
          v-text="displayAmountPerTicket"
        ></span>
        <span
          class="ticket-count"
          v-text="`x ${transaction.ticketCount}`"
        />
      </template>
      <template v-else>
        <span
          :title="$t('change.bill.amount')"
          class="amount-wrapper"
          v-text="getDisplayAmount"
        />
        <span class="pencil"></span>
      </template>
    </p>
    <p v-if="tariff.name"><strong>{{ tariff.name }}</strong></p>
    <template v-if="tariff.description">
      <cs-spoiler v-model="isShownSpoiler" background="transparent">
        <button slot="header" class="icon-info"></button>
        <div class="tariff-description">
          <p>{{ tariff.description }}</p>
        </div>
      </cs-spoiler>
    </template>
    <template v-if="tariff.subscription">
      <p v-if="isSubscriptionTrialDays" v-m-y="4">
        <span
          class="subscription-trial"
          v-text="displaySubscriptionTrialDays"
        ></span>
      </p>
      <p
        v-if="isShownSubscriptionPer"
        v-text="getDisplaySubscriptionPer(tariff.subscription)"
      ></p>
      <p
        v-if="tariff.subscription.oneTimePayment"
        v-text="displayOneTimePayment"
      ></p>
    </template>
    <promo-code
      v-if="isShownPromoCode"
      :content="promoCode"
      :currency="tariff.currency"
    />
    <transaction-tips
      v-else-if="isShownTransactionTips"
      :currency="tariff.currency"
      :tips="transaction.transactionTips"
    />
    <p v-if="tariff.contentUrl" class="tariff-content-url">
      <a @click="$openLink(getDisplayContentUrl(tariff.contentUrl), true, true)">
        {{ $t('realised.content') }}
      </a>
    </p>
    <template v-if="isBillTicket">
      <ticket-information
        v-m-top="8"
        :tariff="tariff"
        :transaction="transaction"
      />
    </template>
    <template v-else-if="isPurchaseBillTicket">
      <!-- TODO: @danealton это следует вынести в отдельный компонент -->
      <p class="ticket-template">
        <base-icon
          v-m-r="5"
          :size="16"
          name="shopping_coupon"
        />
        <a :href="transaction.ticket_link" target="_blank">{{ $t('bill.ticket') }}</a>
      </p>
    </template>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';

import { formatAmount } from 'lib/utils/amount';

import PromoCode from 'molecules/transaction-card/PromoCode';
import TicketInformation from 'modules/Cards/components/TicketInformation';
import TransactionTips from 'modules/Cards/components/TransactionTips';

import {
  IFRAME_REG_EXP,
  CASUAL,
  PURCHASE_BILL,
  WRITE_OFF_OPTIONS_TEXT
} from '../const';

import { getAttributeFrom } from '../helpers';

export default {
  name: 'BillTariff',
  components: {
    PromoCode,
    TicketInformation,
    TransactionTips,
  },
  props: {
    transaction: {
      type: Object,
      required: true,
    },
    tariff: {
      type: Object,
      required: true,
    },
    permission: {
      type: Boolean,
      required: true,
    },
    promoCode: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isShownSpoiler: false,
    };
  },
  computed: {
    billType() {
      return this.transaction.type;
    },
    isCasualType() {
      return this.billType === CASUAL;
    },
    isPurchaseBill() {
      return this.billType === PURCHASE_BILL;
    },
    isEditableAmount() {
      if (!this.permission) {
        return false;
      }

      if (this.isPurchaseBill) {
        return false;
      }

      const isMulti = this.transaction.multiBill;
      const isCasual = this.isCasualType;

      return isMulti && !isCasual;
    },
    isShownSubscriptionPer() {
      const { subscription } = this.tariff;

      if (this.$validate.isNull(subscription)) {
        return false;
      }

      if (this.isPurchaseBill && subscription.oneTimePayment) {
        return false;
      }

      return true;
    },
    displayOneTimePayment() {
      if (this.isPurchaseBill) {
        return this.$t('paid.without.subscription');
      }

      return this.$t('payment.without.subscription.allowed');
    },
    isShownPromoCode() {
      return this.$validate.isNotEmptyObject(this.promoCode);
    },
    getDisplayAmount() {
      const { billAmount } = this.transaction;
      const { amount, commission, currency } = this.tariff;

      if (this.isPurchaseBill) {
        if (this.$validate.isNull(billAmount)) {
          return this.$t('any.amount');
        }

        return formatAmount(billAmount + commission, currency);
      }

      if (this.$validate.isNull(amount)) {
        return this.$t('any.amount');
      }

      return formatAmount(amount + commission, currency);
    },
    displayAmountPerTicket() {
      const { billAmount, amountPerTicket, currency } = this.transaction;

      if (this.$validate.isNull(billAmount)) {
        return this.$t('any.amount');
      }

      return formatAmount(amountPerTicket, currency);
    },
    isBillTicket() {
      if (!this.$validate.isObjectWithFields(this.tariff, ['arrangement'])) {
        return false;
      }

      return this.$validate.isNotNull(this.tariff.arrangement);
    },
    isPurchaseBillTicket() {
      return this.$validate.isNotNull(this.transaction.ticket_code);
    },
    isShownTransactionTips() {
      return this.$validate.isNotNull(this.transaction.transactionTips);
    },
    isSubscriptionTrialDays() {
      return this.$validate.isNumber(this.tariff.subscription?.trialDays);
    },
    displaySubscriptionTrialDays() {
      if (!this.isSubscriptionTrialDays) {
        return null;
      }

      return this.$t('bill.subscription.details.trial.days', {
        days: this.$tc('pluralize.days', this.tariff.subscription.trialDays),
      });
    },
  },
  methods: {
    getDisplaySubscriptionPer(subscription) {
      return this.$t(WRITE_OFF_OPTIONS_TEXT[subscription.per]);
    },
    getDisplayContentUrl(contentUrl) {
      if (IFRAME_REG_EXP.test(contentUrl)) {
        return getAttributeFrom(contentUrl, 'src');
      }

      return contentUrl;
    },
    onEditAmount(tariffId) {
      if (!this.isEditableAmount) {
        return false;
      }

      this.SET_MODAL_PARAMS({
        type: 'MULTIBILL_EDIT',
        params: {
          show: true,
          bill: this.transaction,
          tariffId,
        },
      });
    },
    ...mapMutations(['SET_MODAL_PARAMS']),
  },
};
</script>

<style lang="sass" scoped>
  @mixin square($val)
    width: $val
    height: $val

  @mixin contentIcons($icon, $color: #212121)
    &::before
      background-image: get-icon($icon, $color, $transactionsContentIcons)

  %icon-bg
    background:
      color: transparent
      repeat: no-repeat
      position: center

  %button-icon
    position: absolute
    +square(2rem)
    @extend %icon-bg

  .content-section
    position: relative
    padding: 26px 2rem 1.4rem 3.6rem

    +adaptive(mobile)
      padding: 1.8rem 2rem 1.8rem 3rem

    &::before
      @extend %button-icon
      content: ''
      left: 0
      top: 20px
      +adaptive(mobile)
        top: 1.3rem

  .icon-info
    @extend %button-icon
    top: 26px
    right: 0
    background-image: get-icon(information, #9B51E0, $transactionsContentIcons)

  .bill-tariff
    +contentIcons(arrow-right)

  .content-section.bill-tariff
    &:before
      width: 26px
      height: 26px
      top: 26px

    p
      font-weight: 400
      font-size: 12px
      line-height: 18px

    p.tariff-amount
      margin-bottom: 4px
      font-weight: 500
      font-size: 18px
      line-height: 26px
      color: #212121

      &.editable
        display: inline-block
        cursor: pointer

        .amount-wrapper
          border-bottom: 1px solid #1e1d2f

        .pencil
          background-image: get-icon(pencil, black)
          background-repeat: no-repeat
          display: inline-block
          vertical-align: text-top
          margin-left: 2px
          width: 20px
          height: 20px

    p.tariff-content-url
      margin-top: 8px

      &::before
        content: ''
        width: 20px
        height: 15px
        display: inline-block
        vertical-align: middle
        background: get-icon(clip, #333) no-repeat
        background-size: contain

      a
        font-weight: 500
        font-size: 14px
        line-height: 20px
        color: #212121
        vertical-align: middle
        text-decoration: underline

        &:hover
          text-decoration: none

    .tariff-description
      margin: 8px 0
      padding: 10px
      background: rgba(40, 41, 61, 0.1)
      border-radius: 10px 0px 10px 10px

  .content-section.description + .content-section.bill-tariff,
  .content-section.bill-tariff + .content-section.bill-tariff,
  .info_box.description + .content-section.bill-tariff,
    padding-top: 0

    &:before
      top: 0

    & .icon-info
      top: 0

  .ticket-count
    +font-type('regular', 12, 18)
    margin-left: 10px
    opacity: 0.5

  .ticket-template
    +flexbox
    +align-items(center)
    +font-type(medium, 14, 20)
    margin-top: 8px

    a
      color: var(--color-black-2)
      text-decoration: underline

      &:hover
        text-decoration: none

  .subscription-trial
    padding: 2px 7px
    background: rgba(40, 41, 61, 0.1)
    border-radius: 20px
</style>
